<script setup lang="ts">
import type { RadioModelValue, VRadioProps } from "./VRadio.types";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, toRef, watch } from "vue";
import { useFieldId } from "../useFieldId";
import { registerRadioInGroup, syncRadioGroup } from "./radioGroupSync";
import { radioContainerCva, radioCustomCva, radioDotCva, radioLabelCva } from "./VRadio.cva";

defineOptions({
  name: "VRadio",
});

const props = defineProps<VRadioProps>();

const emit = defineEmits<{
  "update:modelValue": [RadioModelValue]
}>();

const {
  name = false,
  labelLeft = false,
  labelRight = false,
  labelVariant: _labelVariant = "is-light",
  size = "is-medium",
  loading = false,
  hovered: _hovered = false,
} = props;

const id = useFieldId(() => props.id, { inheritFieldId: false });
const disabledRef = toRef(props, "disabled");
const valueRef = ref(props.modelValue);
const inputRef = ref<HTMLInputElement | null>(null);
const groupName = typeof name === "string" && name !== "" ? name : null;

function syncNativeChecked() {
  if (inputRef.value) {
    inputRef.value.checked = valueRef.value === props.value;
  }
}

let unregisterFromGroup: (() => void) | null = null;
onMounted(() => {
  if (groupName !== null) {
    unregisterFromGroup = registerRadioInGroup(groupName, syncNativeChecked);
  }
});
onBeforeUnmount(() => {
  unregisterFromGroup?.();
});

// If an external change is made to the value, update the internal value
watch(() => props.modelValue, (newValue: RadioModelValue, oldValue: RadioModelValue) => {
  if (newValue === oldValue) {
    return;
  }
  valueRef.value = newValue;
});

// If we changed the internal value, emit the change
let followingBoundValue = false;

watch(valueRef, async (newVal: RadioModelValue, oldVal: RadioModelValue) => {
  if (followingBoundValue) {
    followingBoundValue = false;
    return;
  }
  if (newVal === oldVal || disabledRef.value) {
    return;
  }
  // The value was taken over from the bound value (the watcher above), not chosen by a click. Echoing it back would
  // make every radio of a group emit the group's value, so a handler per radio sees a click on each of them.
  if (newVal === props.modelValue) {
    return;
  }
  emit("update:modelValue", newVal);
  // Controlled use: a parent that does not take the value (it asks for a confirmation first, or refuses it) keeps
  // its own value, so the dot follows the bound value again instead of the click.
  await nextTick();
  if (props.modelValue !== undefined && valueRef.value !== props.modelValue) {
    followingBoundValue = true;
    valueRef.value = props.modelValue;
    // The browser already unchecked the radio of the same name that was selected before; check it again.
    if (groupName !== null) {
      syncRadioGroup(groupName);
    } else {
      syncNativeChecked();
    }
  }
});

function handleClick() {
  if (!disabledRef.value) {
    valueRef.value = props.value;
  }
}

// Compute classes using CVA
const classes = computed(() => {
  return {
    container: radioContainerCva({
      disabled: props.disabled,
    }),
    labelLeft: radioLabelCva({
      position: "left",
      size,
      disabled: props.disabled,
    }),
    labelRight: radioLabelCva({
      position: "right",
      size,
      disabled: props.disabled,
    }),
    custom: radioCustomCva({
      size,
      disabled: props.disabled,
    }),
    dot: radioDotCva({
      size,
      disabled: props.disabled,
      selected: valueRef.value === props.value,
    }),
  };
});
</script>

<template>
  <div
    :class="[
      classes.container,
      $attrs.class,
    ]"
    @click="handleClick"
  >
    <label
      v-if="labelLeft || $slots.labelLeft"
      :for="id"
      :class="classes.labelLeft"
    >
      <VSkeleton v-if="loading" :width="75" :height="10" />
      <template v-else>
        <slot name="labelLeft">{{ labelLeft }}</slot>
      </template>
    </label>
    <div class="relative">
      <VSkeleton
        v-if="loading"
        :width="20"
        width-unit="px"
        :dynamic-width="false"
        :height="20"
      />
      <template v-else>
        <input
          :id="id"
          ref="inputRef"
          v-model="valueRef"
          type="radio"
          class="peer sr-only"
          :value="props.value"
          :name="typeof name === 'string' ? name : ''"
          :disabled="props.disabled"
        >
        <span :class="classes.custom">
          <span
            :class="classes.dot"
          />
        </span>
      </template>
    </div>
    <label
      v-if="labelRight || $slots.labelRight"
      :for="id"
      :class="classes.labelRight"
    >
      <VSkeleton v-if="loading" :width="150" width-unit="px" :height="10" />
      <template v-else>
        <slot name="labelRight">{{ labelRight }}</slot>
      </template>
    </label>
  </div>
</template>
